// src/App.jsx
import React, { useContext, useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, useTheme, useMediaQuery } from '@mui/material';

import { AuthProvider } from './contexts/AuthContext';
import { AuthContext } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Sidebar from './components/layout/Sidebar';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/dashboard/Dashboard';
import AssessmentForm from './components/assessment/AssessmentForm';
import ResultsView from './components/assessment/ResultsView';
import NotFound from './components/common/NotFound';
import AssessmentHistory from './components/assessment/AssessmentHistory';
import HelpPage from './components/help/HelpPage';
import LandingPage from './components/landing/LandingPage';
import AboutPage from './components/pages/AboutPage';
import ContactPage from './components/pages/ContactPage';
import TermsOfServicePage from './components/pages/TermsOfServicePage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import CookiePolicyPage from './components/pages/CookiePolicyPage';
import AdminReviewsManager from './components/dashboard/AdminReviewsManager';
import AdminUserManagement from './components/dashboard/AdminUserManagement';
import AdminMetricsManager from './components/dashboard/AdminMetricsManager';
import AnalyticsDashboard from './components/dashboard/AnalyticsDashboard';
import metricsService from './services/metricsService';


// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

function AppContent() {
  const { currentUser } = useContext(AuthContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const lastPath = useRef(window.location.pathname);
  const hasTrackedInit = useRef(false);
  const sessionStartTime = useRef(Date.now());

  // Map routes to feature names for auto-tracking
  const getFeatureName = (path) => {
    if (path.startsWith('/assessment') || path.startsWith('/assess')) return 'risk_assessment';
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/analytics')) return 'analytics';
    if (path.startsWith('/help')) return 'help';
    if (path.startsWith('/admin/metrics')) return 'admin_metrics';
    if (path.startsWith('/admin/users')) return 'admin_users';
    if (path.startsWith('/admin/reviews')) return 'admin_reviews';
    return null;
  };

  // --- Auto-tracking: visits, clicks, features, sessions ---
  useEffect(() => {
    // Guard against React StrictMode double-mount
    if (hasTrackedInit.current) return;
    hasTrackedInit.current = true;

    // Track page view on first load
    metricsService.trackImpression();

    // Track feature on first load
    const feature = getFeatureName(window.location.pathname);
    if (feature) metricsService.trackFeature(feature);

    // Send session duration on page unload
    const handleUnload = () => {
      const durationMin = parseFloat(((Date.now() - sessionStartTime.current) / 1000 / 60).toFixed(2));
      metricsService.trackSessionEnd(durationMin);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Click tracking — only counts actual user clicks
  useEffect(() => {
    const handleClick = (e) => {
      if (e.isTrusted) {
        const target = e.target.closest('a, button, [role="button"]');
        if (target) {
          metricsService.trackClick();
        }
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Track page view + feature on route change
  useEffect(() => {
    if (window.location.pathname !== lastPath.current) {
      lastPath.current = window.location.pathname;
      metricsService.trackImpression();
      const feature = getFeatureName(window.location.pathname);
      if (feature) metricsService.trackFeature(feature);
    }
  });

  const isAuthPage = window.location.pathname === '/login' || window.location.pathname === '/register';
  const isLandingPage = window.location.pathname === '/' ||
    window.location.pathname === '/landing' ||
    window.location.pathname === '/reviews' ||
    window.location.pathname === '/about' ||
    window.location.pathname === '/contact' ||
    window.location.pathname === '/terms' ||
    window.location.pathname === '/privacy-policy' ||
    window.location.pathname === '/cookie-policy' ||
    window.location.pathname === '/analytics';

  const showSidebar = currentUser && !isAuthPage && !isLandingPage;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header - Only show for authenticated app pages, not landing or public pages */}
      {currentUser && !isAuthPage && !isLandingPage && (
        <Header
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
      )}

      <Box sx={{ display: 'flex', flex: 1, position: 'relative' }}>
        {/* Responsive Sidebar */}
        {showSidebar && (
          <Sidebar
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
          />
        )}

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minHeight: 'calc(100vh - 64px)', // Account for header height
            backgroundColor: isLandingPage ? 'transparent' : 'background.default',
            // Responsive padding
            p: isLandingPage ? 0 : {
              xs: 1,  // Minimal padding on mobile
              sm: 2,  // Medium padding on tablet
              md: 3   // Full padding on desktop
            },
            // Responsive positioning for sidebar
            ...(showSidebar && !isMobile && {
              ml: '240px', // Push content right when sidebar is permanent
              width: 'calc(100% - 240px)'
            }),
            // Top padding to account for fixed header
            pt: isLandingPage ? 0 : {
              xs: 1, // Minimal top padding on mobile
              sm: 2,
              md: 3
            },
            // Prevent horizontal scroll on mobile
            overflowX: 'hidden',
            // Smooth transitions
            transition: muiTheme.transitions.create(['margin', 'width'], {
              easing: muiTheme.transitions.easing.sharp,
              duration: muiTheme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/reviews" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/new-assessment"
              element={
                <ProtectedRoute>
                  <AssessmentForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessment/:id"
              element={
                <ProtectedRoute>
                  <ResultsView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <AssessmentHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <ProtectedRoute>
                  <AdminReviewsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <AdminUserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/metrics"
              element={
                <ProtectedRoute>
                  <AdminMetricsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <HelpPage />
                </ProtectedRoute>
              }
            />

            {/* Redirect authenticated users trying to access landing to dashboard */}
            <Route
              path="/app"
              element={
                currentUser ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>

        {/* Mobile backdrop overlay when sidebar is open */}
        {showSidebar && isMobile && mobileOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: muiTheme.zIndex.drawer - 1,
              cursor: 'pointer'
            }}
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation menu"
          />
        )}
      </Box>

      {/* Footer - Only show for authenticated pages */}
      {!isLandingPage && <Footer />}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;